"use client";

import { useState, type FormEvent } from "react";
import { Button } from "../common/button";
import { Input } from "../common/input";
import { Textarea } from "../common/Textarea";
import { Select } from "../common/select";
import {
  SUPPORT_CATEGORY_LABELS,
  type PickerConfig,
  type SupportPrefill,
  type SupportTicketCategory,
} from "./types";

interface SupportTicketFormProps {
  prefill: SupportPrefill;
  pickers?: PickerConfig[];
  /** API route to POST to. Defaults to /api/support/ticket. */
  endpoint?: string;
  /** Called after a successful submission. */
  onSuccess?: (result: {
    id: string;
    ticketNumber: number;
    accessToken: string;
  }) => void;
}

function gatherBrowserContext(appVersion?: string | null) {
  if (typeof window === "undefined") return {};
  return {
    url: window.location.href,
    referrer: document.referrer || null,
    userAgent: navigator.userAgent,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    locale: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    appVersion: appVersion ?? null,
  };
}

export function SupportTicketForm({
  prefill,
  pickers = [],
  endpoint = "/api/support/ticket",
  onSuccess,
}: SupportTicketFormProps) {
  const isAnon = !prefill.userId && !prefill.proId;

  const [category, setCategory] = useState<SupportTicketCategory>("QUESTION");
  const [customCategory, setCustomCategory] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [submitterEmail, setSubmitterEmail] = useState(
    prefill.submitterEmail ?? ""
  );
  const [submitterName, setSubmitterName] = useState(
    prefill.submitterName ?? ""
  );
  const [pickerValues, setPickerValues] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successInfo, setSuccessInfo] = useState<{
    ticketNumber: number;
    accessToken: string;
  } | null>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (!subject.trim() || !body.trim()) {
      setError("Subject and description are both required.");
      return;
    }
    if (isAnon && !submitterEmail.trim()) {
      setError("Please provide an email so we can reach you.");
      return;
    }
    if (category === "OTHER" && !customCategory.trim()) {
      setError("Please describe the category.");
      return;
    }
    for (const picker of pickers) {
      if (!picker.optional && !pickerValues[picker.field]) {
        setError(`Please select: ${picker.label}`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload = {
        sourceApp: prefill.sourceApp,
        category,
        customCategory: category === "OTHER" ? customCategory.trim() : null,
        subject: subject.trim(),
        body: body.trim(),
        userId: prefill.userId ?? null,
        proId: prefill.proId ?? null,
        submitterEmail: isAnon
          ? submitterEmail.trim()
          : (prefill.submitterEmail ?? null),
        submitterName: isAnon
          ? submitterName.trim() || null
          : (prefill.submitterName ?? null),
        episodeId: pickerValues.episodeId || null,
        groupId: pickerValues.groupId || null,
        sessionId: pickerValues.sessionId || null,
        companyId: pickerValues.companyId || null,
        context: gatherBrowserContext(prefill.appVersion),
      };

      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        if (res.status === 429) {
          throw new Error(
            "You've sent too many tickets recently. Please wait a bit and try again."
          );
        }
        throw new Error(data.error ?? "Something went wrong. Please try again.");
      }

      const data = (await res.json()) as {
        id: string;
        ticketNumber: number;
        accessToken: string;
      };
      setSuccessInfo({
        ticketNumber: data.ticketNumber,
        accessToken: data.accessToken,
      });
      onSuccess?.(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (successInfo) {
    return (
      <div className="rounded-lg border border-green-200 bg-green-50 p-6 text-center">
        <h3 className="text-lg font-semibold text-green-900">
          Ticket #{successInfo.ticketNumber} received
        </h3>
        <p className="mt-2 text-sm text-green-800">
          Thanks for reaching out. We'll reply by email
          {isAnon ? " at the address you provided." : "."}
        </p>
        {isAnon && (
          <p className="mt-3 text-xs text-green-700">
            We'll also email you a link to view this ticket and respond:
            <br />
            <code className="break-all">
              /support/t/{successInfo.accessToken}
            </code>
          </p>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </div>
      )}

      <Select
        label="Category"
        value={category}
        onChange={(e) => setCategory(e.target.value as SupportTicketCategory)}
        options={Object.entries(SUPPORT_CATEGORY_LABELS).map(
          ([value, label]) => ({ value, label })
        )}
      />

      {category === "OTHER" && (
        <Input
          label="Describe the category"
          placeholder="e.g. Mobile app crash on iPad"
          value={customCategory}
          onChange={(e) => setCustomCategory(e.target.value)}
          required
        />
      )}

      {pickers.map((picker) => (
        <Select
          key={picker.field}
          label={picker.label}
          value={pickerValues[picker.field] ?? ""}
          onChange={(e) =>
            setPickerValues((p) => ({
              ...p,
              [picker.field]: e.target.value,
            }))
          }
          placeholder={
            picker.optional ? "(Optional — none)" : "Select an option..."
          }
          options={picker.options}
        />
      ))}

      <Input
        label="Subject"
        placeholder="Brief summary"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        required
      />

      <Textarea
        label="What's going on?"
        placeholder="Please describe the issue in detail. Screenshots help if you have them."
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={6}
        required
      />

      {isAnon && (
        <>
          <Input
            label="Your email"
            type="email"
            placeholder="you@example.com"
            value={submitterEmail}
            onChange={(e) => setSubmitterEmail(e.target.value)}
            required
            helpText="We'll send replies here and a link to follow the ticket."
          />
          <Input
            label="Your name (optional)"
            placeholder="First name"
            value={submitterName}
            onChange={(e) => setSubmitterName(e.target.value)}
          />
        </>
      )}

      <div className="flex justify-end pt-2">
        <Button type="submit" isLoading={submitting}>
          Send ticket
        </Button>
      </div>
    </form>
  );
}
